import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../../../services/api';
import {
  type WaitlistListApiResponse,
  type WaitlistListResponse,
  type WaitlistFilters,
  mapWaitlistListFromApi,
} from '../types/waitlist.types';

// Query key factory
const waitlistKeys = {
  all: ['waitlist'] as const,
  list: (filters: WaitlistFilters) => ['waitlist', 'list', filters] as const,
  listNames: () => ['waitlist', 'list-names'] as const,
};

// Fetchers

async function fetchWaitlistEntries(filters: WaitlistFilters): Promise<WaitlistListResponse> {
  const params: Record<string, string | number> = {};

  if (filters.listName) {
    params.list_name = filters.listName;
  }
  if (filters.search) {
    params.search = filters.search;
  }
  if (filters.page !== undefined) {
    params.offset = ((filters.page || 1) - 1) * (filters.limit || 50);
  }
  if (filters.limit) {
    params.limit = filters.limit;
  }
  if (filters.sortBy) {
    params.sort_by = filters.sortBy;
  }
  if (filters.sortOrder) {
    params.sort_order = filters.sortOrder;
  }

  const data = await apiGet<WaitlistListApiResponse>('/api/admin/waitlist', params);
  return mapWaitlistListFromApi(data);
}

async function fetchListNames(): Promise<string[]> {
  return apiGet<string[]>('/api/admin/waitlist/list-names');
}

// React Query hooks

export function useWaitlistEntries(
  filters: WaitlistFilters
): UseQueryResult<WaitlistListResponse, Error> {
  return useQuery({
    queryKey: waitlistKeys.list(filters),
    queryFn: () => fetchWaitlistEntries(filters),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useWaitlistListNames(): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: waitlistKeys.listNames(),
    queryFn: fetchListNames,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}
